import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, Tab, Box } from "@mui/material";
import IndividualReminders from "./IndividualReminders";
import TodaysDue from "./TodaysDue";
import Pagination from "../Components/Pagination";
import { IoIosSearch } from "react-icons/io";

function ReminderManagement({ isSidebarExpanded }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const normalizeTab = (tab) => {
    if (tab === 4) return 3; // Preserve old URL mapping: custom(4) -> custom(3)
    return [0, 1, 2, 3].includes(tab) ? tab : 0;
  };
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    return normalizeTab(tabParam ? parseInt(tabParam, 10) : 0);
  });
  const [tabSearch, setTabSearch] = useState({
    0: "",
    1: "",
    2: "",
    3: "",
  });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // const handleTabChange = (event, newValue) => {
  //   setActiveTab(newValue);
  // };

  // Map tab index -> API filter
  const TAB_TO_FILTER = {
    0: "All",
    1: "today",
    2: "overdue",
    3: "custom",
  };

  const handlePaginationChange = (newPage, newPerPage) => {
    setPage(newPage);
    setItemsPerPage(newPerPage);
  };

  // ---- CUSTOM range defaults: current month ----
  const today = useMemo(() => new Date(), []);
  const firstOfMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today]
  );
  const lastOfMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 1, 0),
    [today]
  );

  // store as YYYY-MM-DD strings
  const toYmd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  const [customStart, setCustomStart] = useState(toYmd(firstOfMonth));
  const [customEnd, setCustomEnd] = useState(toYmd(lastOfMonth));

  // "Applied" values that actually get sent to API when user clicks Apply
  const [appliedStart, setAppliedStart] = useState(toYmd(firstOfMonth));
  const [appliedEnd, setAppliedEnd] = useState(toYmd(lastOfMonth));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue }); // Updates URL to ?tab=1
    setPage(1);
    setTotalRecords(0);

    if (TAB_TO_FILTER[newValue] === "custom") {
      // Always reset to current month defaults when switching to Custom tab
      const defaultStart = toYmd(firstOfMonth);
      const defaultEnd = toYmd(lastOfMonth);

      setCustomStart(defaultStart);
      setCustomEnd(defaultEnd);
      setAppliedStart(defaultStart);
      setAppliedEnd(defaultEnd);
    }
  };

  const handleApplyCustom = () => {
    setPage(1);
    setTotalRecords(0);

    if (customStart && customEnd && customStart > customEnd) {
      setAppliedStart(customEnd);
      setAppliedEnd(customStart);
    } else {
      setAppliedStart(customStart);
      setAppliedEnd(customEnd);
    }
  };

  const handleClearCustom = () => {
    const defaultStart = toYmd(firstOfMonth);
    const defaultEnd = toYmd(lastOfMonth);

    setCustomStart(defaultStart);
    setCustomEnd(defaultEnd);
    setAppliedStart(defaultStart);
    setAppliedEnd(defaultEnd);
    setPage(1);
    setTotalRecords(0);
  };

  const activeSearch = tabSearch[activeTab] || "";

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTabSearch((prev) => ({
      ...prev,
      [activeTab]: value,
    }));
  };

  return (
    <div
      className={`table-container reminder-page-layout ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="reminder-management">
        <Box sx={{ width: "100%" }} className="reminder-shell">
          <div className="table-header">
            <div className="table-header-content">
              <div className="table-left-side">
                <h2 className="jost-bold">Individual Client Reminder Management</h2>
              </div>
              <div className="table-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search ..."
                    className="search-bar"
                    value={activeSearch}
                    onChange={handleSearchChange}
                  />
                  <IoIosSearch className="search-bar-icon" />
                </div>
              </div>
            </div>
          </div>

          {/* ---------- Tabs + Calendar in one row ---------- */}
          <div className="individual-reminder-tabs-row">
            {/* Tabs on left */}
            <Tabs
              className="individual-reminder-tabs"
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="Reminder tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#007bff",
                  height: "4px",
                  borderRadius: "4px",
                },
              }}
            >
              <Tab label="All Due" />
              <Tab label="Today" />
              <Tab label="Overdue" />
              <Tab label="Custom" />
            </Tabs>

            {/* Calendar controls on right (only if custom tab is active) */}
            {TAB_TO_FILTER[activeTab] === "custom" && (
              <div className="individual-reminder-custom-controls">
                <div className="individual-reminder-date-field">
                  <label>
                    Start Date
                  </label>
                  <input
                    className="individual-reminder-date-input"
                    type="date"
                    value={customStart}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val) {
                        const [y, m, d] = val.split("-");
                        if (y && y.length > 4) {
                          val = `${y.slice(0, 4)}-${m || "01"}-${d || "01"}`;
                        }
                      }
                      setCustomStart(val);
                    }}
                  />
                </div>

                <div className="individual-reminder-date-field">
                  <label>
                    End Date
                  </label>
                  <input
                    className="individual-reminder-date-input"
                    type="date"
                    value={customEnd}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val) {
                        const [y, m, d] = val.split("-");
                        if (y && y.length > 4) {
                          val = `${y.slice(0, 4)}-${m || "01"}-${d || "01"}`;
                        }
                      }
                      setCustomEnd(val);
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleApplyCustom}
                  className="reminder-btn-apply"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
          {/* ------------------------------------------------ */}

          <Box sx={{ padding: 2 }} className="reminder-tab-content">
            {activeTab === 0 ? (
              <IndividualReminders
                isSidebarExpanded={isSidebarExpanded}
                filterValue="All"
                search={tabSearch[0] || ""}
                page={page}
                itemsPerPage={itemsPerPage}
                onTotalChange={setTotalRecords}
              />
            ) : activeTab === 1 ? (
              <TodaysDue
                isSidebarExpanded={isSidebarExpanded}
                search={tabSearch[1] || ""}
                page={page}
                itemsPerPage={itemsPerPage}
                onTotalChange={setTotalRecords}
              />
            ) : activeTab === 2 ? (
              <IndividualReminders
                isSidebarExpanded={isSidebarExpanded}
                filterValue={TAB_TO_FILTER[activeTab]}
                search={tabSearch[2] || ""}
                page={page}
                itemsPerPage={itemsPerPage}
                onTotalChange={setTotalRecords}
              />
            ) : activeTab === 3 ? (
              <IndividualReminders
                isSidebarExpanded={isSidebarExpanded}
                filterValue={TAB_TO_FILTER[activeTab]}
                startDate={appliedStart}
                endDate={appliedEnd}
                search={tabSearch[3] || ""}
                page={page}
                itemsPerPage={itemsPerPage}
                onTotalChange={setTotalRecords}
              />
            ) : null
            }
          </Box>

          <Pagination
            className="ledger-pagination reminder-pagination"
            total={totalRecords}
            showPerPage={itemsPerPage}
            onPaginationChange={handlePaginationChange}
          />
        </Box>
      </div>
    </div>
  );
}

export default ReminderManagement;
